import { computed, getCurrentInstance, reactive, type ComputedRef, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import { getVideojs, getVideojsControlBar, updateVideojsTitleBarText } from 'src/lib/videojsHelper';
import { dtFormat } from 'src/lib/dateHelper';
import type { LeftRight, MoviePlayerCompareModeState } from 'src/models/moviePlayer';

const PLAYER_TYPE = 'compare';
// 動画プレイヤーの表示アスペクト比（16:9固定）。
// 実行中に変化しない値のためモジュール定数として定義する。
const VIDEO_ASPECT_RATIO = 16 / 9;

// prepareVideoPlayers実行時に、
// video.jsのtimeupdate/canplayイベントやカスタムコントロールボタンのクリックから
// 呼び出すハンドラ群。
// 本ファイルはuseMoviePlayerCompareModeControl.ts/useMoviePlayerCompareModeTimeUpdate.tsを
// 一切importせず、
// 実処理は呼び出し元（useMoviePlayerCompareModePlayback.tsのファサード）からこの引数経由で
// 注入してもらう。
// これにより vjs→control→vjs
// や vjs→timeUpdate→movieTransition→vjs のような循環参照を避ける。
export interface VjsControlHandlers {
  onTimeUpdate: (lr: LeftRight, vjs: VideoJsPlayer) => void;
  onTickMovieBackward: () => void;
  onPlayUI: () => void;
  onPauseUI: () => void;
  onTickMovieForward: () => void;
  onPlayModeChange: (isPlayBackward: boolean) => void;
}

interface MoviePlayerCompareModeVjsContext {
  vid1: ComputedRef<string>;
  vid2: ComputedRef<string>;
  videoAspectRatio: number;
  getVjsForVid: (vid: string) => VideoJsPlayer | undefined;
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
  updateTitleBarTexts: (lr: LeftRight) => void;
  prepareVideoPlayers: (handlers: VjsControlHandlers) => Promise<void>;
}

// useMoviePlayerCompareModePlayback.tsから分割したvideo.jsインスタンス生成・DOM配線を担当する。
// vjsMap/vjsMapLRは本ファイルのプライベート状態であり、
// 他の関心事（逆再生・共通再生制御・動画切替・
// timeupdateハンドラ）はそれぞれ別ファイルに分割し、
// getVjsForLr等を通じてのみ本ファイルの状態にアクセスする。
// 他分割composableに依存する処理
// （コントロールボタンのクリック・timeupdate時に呼ぶ関数）は持たず、
// prepareVideoPlayersの引数として受け取るだけにすることで、
// 本ファイルは分割グラフの末端（依存されるだけ）に留める。
export function useMoviePlayerCompareModeVjs(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
): UnwrapNestedRefs<MoviePlayerCompareModeVjsContext> {
  const uid = getCurrentInstance()?.uid;
  const vid1 = computed(() => `vid1-${uid}`);
  const vid2 = computed(() => `vid2-${uid}`);
  const videoAspectRatio = VIDEO_ASPECT_RATIO;

  const vjsMap = new Map<string, VideoJsPlayer>();
  const vjsMapLR = new Map<LeftRight, VideoJsPlayer>();

  const getVjsForVid = (vid: string) => vjsMap.get(vid);
  const getVjsForLr = (lr: LeftRight) => vjsMapLR.get(lr);

  const updateTitleBarTexts = (lr: LeftRight) => {
    const mgi = state[lr].geo.currentMgi;
    if (!mgi) return;

    const dateDisp = dtFormat(mgi.ts, 'yyyy/mm/dd HH:MM:SS') + '~';
    const locationDisp = mgi.locationDisp;
    const vjs = vjsMapLR.get(lr);
    if (vjs) {
      updateVideojsTitleBarText(vjs, { dateDisp, locationDisp });
    }
  };
  const getVideoJsDefaultOptions = (): VideoJsPlayerOptions => {
    // https://docs.videojs.com/tutorial-components.html
    return {
      // autoplay: true,
      // TODO: which on is this.
      aspectRatio: '16:9', // this.aspectRatio,
      loop: false,
    };
  };
  const setVideojsEventListeners = (vjs: VideoJsPlayer, handlers: VjsControlHandlers) => {
    const controlBar = getVideojsControlBar(vjs).el();
    const tickMovieBackwardBtn = controlBar.querySelector('.vjs-tick-movie-backward');
    const playMovieBtn = controlBar.querySelector('.vjs-play-movie');
    const pauseMovieBtn = controlBar.querySelector('.vjs-pause-movie');
    const tickMovieForwardBtn = controlBar.querySelector('.vjs-tick-movie-forward');
    const playMovieBackwardCheckbox = controlBar.querySelector('.vjs-play-movie-backward');
    if (
      !tickMovieBackwardBtn
      || !playMovieBtn
      || !pauseMovieBtn
      || !tickMovieForwardBtn
      || !playMovieBackwardCheckbox
    ) return;
    tickMovieBackwardBtn.addEventListener('click', () => handlers.onTickMovieBackward(), false);
    playMovieBtn.addEventListener('click', () => handlers.onPlayUI(), false);
    pauseMovieBtn.addEventListener('click', () => handlers.onPauseUI(), false);
    tickMovieForwardBtn.addEventListener('click', () => handlers.onTickMovieForward(), false);
    playMovieBackwardCheckbox.addEventListener('click', () => handlers.onPauseUI(), false);
    playMovieBackwardCheckbox.addEventListener('change', () =>
      handlers.onPlayModeChange((playMovieBackwardCheckbox as HTMLInputElement).checked), false);
  };
  const prepareVideojs = (
    lr: LeftRight,
    vid: string,
    handlers: VjsControlHandlers,
  ): Promise<[LeftRight, string, VideoJsPlayer]> => {
    return new Promise((resolve) => {
      const vjs = getVideojs(vid, getVideoJsDefaultOptions(), PLAYER_TYPE);
      setVideojsEventListeners(vjs, handlers);
      vjs.ready(() => {
        vjs.muted(false);
        vjs.volume(0.0);
        resolve([lr, vid, vjs]);
      });
    });
  };
  const prepareVideoPlayers = async(handlers: VjsControlHandlers) => {
    const objs: { vid: string; lr: LeftRight }[] = [
      { vid: vid1.value, lr: 'left' },
      { vid: vid2.value, lr: 'right' },
    ];
    const vjsArr = await Promise.all(objs.map(({ lr, vid }) => {
      return prepareVideojs(lr, vid, handlers);
    }));
    vjsArr.forEach(([lr, vid, vjs]) => {
      vjsMap.set(vid, vjs);
      vjsMapLR.set(lr, vjs);
      state.ui.activeVjsLrMap[lr] = false;
    });
    // vjsがevtから取れなそうなので昔のfunctionの書き方でやる.
    const vjsLeft = vjsMapLR.get('left');
    const vjsRight = vjsMapLR.get('right');
    if (!vjsLeft || !vjsRight) return;

    vjsLeft.on('timeupdate', () => {
      const vjs = vjsLeft;
      handlers.onTimeUpdate('left', vjs);
    });
    vjsRight.on('timeupdate', () => {
      const vjs = vjsRight;
      handlers.onTimeUpdate('right', vjs);
    });
    // 再生速度
    vjsLeft.on('canplay', () => {
      const vjs = vjsLeft;
      vjs.playbackRate(state.left.playback.currentPlaySpeed);
    });
    vjsRight.on('canplay', () => {
      const vjs = vjsRight;
      vjs.playbackRate(state.right.playback.currentPlaySpeed);
    });
  };

  return reactive({
    vid1,
    vid2,
    videoAspectRatio,
    getVjsForVid,
    getVjsForLr,
    updateTitleBarTexts,
    prepareVideoPlayers,
  });
}
